import React, { useContext, useEffect, useState } from "react";
import IRoute from "../../../models/IRoute";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import { OwFnInput } from "../../shared/OwFnInput/OwFnInput";
import { useFormik } from "formik";
import INewRoute from "../../../models/INewRoute";
import OwButton from "../../shared/OwButton/OwButton";
import IProcessRouteDTO from "../../../models/DTO/IProcessRouteDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import { SocketContext } from "../../../contexts/SocketContext";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnProcessRouteDTO from "../../../models/DTO/IOnProcessRouteDTO";
import UiContext from "../../../contexts/UiContext";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import { TypeaheadFn } from "../../shared/TypeaheadFn/TypeaheadFn";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "../../../services/RestService";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IConnection_Token from "../../../models/IConnection_Token";
import IOnTypeaheadLookupByTokenTypeDTO from "../../../models/DTO/IOnTypeaheadLookupByTokenTypeDTO";
import TokenTypesEnum from "../../../models/TokenTypesEnum";
import IUser from "@models/IUser";

type Props = {
    route?: IRoute;
    cancelFn: (refresh?: boolean) => void;
};

export const RouteSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const [route, setRoute] = useState<IRoute | INewRoute | undefined>(props.route);
    const [isDisabled, setIsDisabled] = useState(true);
    const [availableUsers, setAvailableUsers] = useState<IConnection_Token[]>([]);
    const [selectedDefaultDriver, setSelectedDefaultDriver] = useState<IUser | undefined>(
        props.route?.Default_User ? props.route.Default_User : undefined
    );

    const cancel = () => {
        props.cancelFn();
    };

    const searchUsers = async (name: string) => {
        if (!name.trim()) {
            setAvailableUsers([]);
            return;
        }

        const url = buildRestUrlWithQuery({
            endpoint: RestEndpointsEnum.TokensSearch,
            query: {
                tokenType: TokenTypesEnum.SUPPLIER_USER,
                name,
                limit: 10,
            },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
        });

        try {
            const response = await fetch(url, { method: "GET", headers });
            if (response.ok) {
                const data = (await response.json()) as IOnTypeaheadLookupByTokenTypeDTO;
                setAvailableUsers(data.results || []);
            }
        } catch (error) {
            console.error("Error searching users:", error);
            setAvailableUsers([]);
        }
    };

    const handleUserSelected = (user: IUser) => {
        setSelectedDefaultDriver(user);
        setAvailableUsers([]);
    };

    const clearDefaultDriver = () => {
        setSelectedDefaultDriver(undefined);
    };

    const renderAvailableUser = (user: IUser) => {
        return (
            <div>
                <div>{user?.name}</div>
                {user?.email && <small className="text-muted">{user.email}</small>}
            </div>
        );
    };

    const processRoute = (values: { name: string; is_pickup: boolean; default_start_time: string }) => {
        const payload: IProcessRouteDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            route: {
                name: values.name,
                is_pickup: values.is_pickup,
                default_user_id: selectedDefaultDriver?.id || null,
                default_start_time: values.default_start_time || null,
                guid: props.route ? props.route.guid : undefined,
            },
        };

        socket.emit(EndpointsEnum.RoutesProcessRoute, payload);
    };

    const onProcessRoute = (data: IOnProcessRouteDTO) => {
        const notification: INotification = {
            message: "successfully_processed_route",
            level: NotificationLevelEnum.success,
            position: NotificationPositionEnum.bc,
        };

        uiContext.notifyFn(null, notification);

        props.cancelFn(true);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.RoutesOnProcessRoute, fn: onProcessRoute }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const formik = useFormik({
        initialValues: {
            name: route?.name || "",
            is_pickup: route?.is_pickup || false,
            default_start_time: route?.default_start_time || "",
        },
        validateOnMount: true,
        onSubmit: (values) => {
            // props.getTransportOptions(values);
            processRoute(values);
        },
        validate: (values) => {
            const errors: any = {};

            if (!values.name) {
                errors.name = "Required";
            }

            setIsDisabled(Object.keys(errors).length > 0);

            return errors;
        },
    });

    return (
        <div className={"RouteSidebar p-2"}>
            <div className={"row"}>
                <div className={"col-12"}>
                    <div className="text-end">
                        <button className="btn btn-danger btn-sm" onClick={cancel}>
                            <i className="far fa-times" />
                        </button>
                    </div>
                    <OwTitle className={"mb-2"} leftIconName={"fad fa-road"} title={"route"} />
                    <hr />
                </div>
            </div>

            <div className="row mt-2">
                <form onSubmit={formik.handleSubmit}>
                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"name"} isBold={true} />
                        </div>
                        <div className={""}>
                            <OwFnInput
                                className={"form-control form-control-sm"}
                                fn={formik.handleChange}
                                name={"name"}
                                value={formik.values.name}
                            />
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwToggle
                                name={"is_pickup"}
                                fn={formik.handleChange}
                                active={formik.values.is_pickup}
                                label={"is_pickup"}
                                labelOnRight={true}
                            />
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"default_start_time"} isBold={true} />
                        </div>
                        <div>
                            <input
                                type="time"
                                className="form-control form-control-sm"
                                style={{ width: "130px" }}
                                name="default_start_time"
                                value={formik.values.default_start_time}
                                onChange={formik.handleChange}
                            />
                        </div>
                        <div>
                            <small className="text-muted">
                                <OwLabel title={"default_start_time_help"} noBottomMargin={true} />
                            </small>
                        </div>
                    </div>

                    <div className={"d-flex flex-column mb-4"}>
                        <div>
                            <OwLabel title={"default_driver"} isBold={true} />
                        </div>
                        <div>
                            <TypeaheadFn
                                lookupFn={searchUsers}
                                availableOptions={availableUsers.map((token) => token.User).filter(Boolean) as IUser[]}
                                selectedOption={selectedDefaultDriver}
                                valueProp="id"
                                selectedOptionFn={handleUserSelected}
                                emitSelectionSetup={{ emitObject: true }}
                                titleProp="name"
                                subTitleProp="email"
                                renderFn={renderAvailableUser}
                                placeholder="search_for_driver"
                                clearSelectionFn={clearDefaultDriver}
                            />
                        </div>
                    </div>

                    <div>
                        <OwButton type={"submit"} title={"save"} disableOnActive={true} disabled={isDisabled} />
                    </div>
                </form>
            </div>
        </div>
    );
};
